import { types, getEnv, flow, Instance, SnapshotOut } from 'mobx-state-tree';
import {
    IGroupedApiCtx, ISportsBettingSessionApiForDesktop, ReportedProblemType, TodayTicketsStore, CashOutListStore, SoundUI,
    TicketDetailStore, defaultTicketDetailStore, FindTicketByNumberStore, chatDtos, NotificationHandlerKeys,
    ChatListStore, ListPagerModel, cashOutDtos, ConnectionStatus, ProgressStateModel, ILoggingService, TicketListTypeSelectorStore, TicketListTypeSelectorItemStore, defaultTicketDetailForVGStore, TicketDetailForVGStore, createGlobalRenderTelemetryService,
    gAService, ScreenInfo, ApplicationStoreWithFooterVisibility, MtsTicketStore,
    devLogService,
    IChatMessagesChangeData
} from 'eTip--common';
import { LiveEventListStore } from './models/live/liveEventList';
import { ILiveAppEnv } from './liveAppEnvironment';
import { LiveEventStore } from './models/live/liveEvent';
import { FavoriteEventDetailsStore } from './models/live/favoriteEventDetails';
import { CalendarStore } from './models/live/calendar';
import { ILiveCategoryStore } from './models/live/liveCategory';
import { TicketListStore, TicketListForVGStore, RightColumnBannerStore } from 'eTip--common';
import { SearchStore } from './models/live/searchStore';
import { observable } from 'mobx';
import { FavoriteEventInfo } from './models/live/favoriteEventInfo';
import { paths } from './routing';
import { translationForMts } from '../../localization/localization';
import { MarketingActionStoreDesktop } from './models/marketingActionStore';
import { betDetailStore } from './models/betDetail';

export { Provider } from 'mobx-react';

const favoriteEventIdsStorageKey = 'favorite-events';

export type IApplicationStore = Instance<typeof ApplicationStore>;
export const ApplicationStoreKey = 'applicationStore';
export const ApplicationStore = types.compose(ApplicationStoreWithFooterVisibility,
    types.model(ApplicationStoreKey, {
        isOfflineCheckInProgress: types.optional(types.boolean, false),
        events: types.optional(LiveEventListStore, {}),
        search: types.optional(SearchStore, <Instance<typeof SearchStore>>{}),
        eventDetail: types.optional(LiveEventStore, {}),
        favoriteDetails: types.optional(FavoriteEventDetailsStore, {}),
        calendar: types.optional(CalendarStore, {}),
        ticketWrapper: types.maybeNull(MtsTicketStore), //for __IsMTS__ only
        betDetail: types.optional(betDetailStore, <SnapshotOut<typeof betDetailStore>>{}), // for __IsMTSTipos__ only TODOHMTS
        cashOutList: types.optional(CashOutListStore, {
            isLoading: true,
            isAvailable: false
        } as Instance<typeof CashOutListStore>),
        screenInfo: types.optional(ScreenInfo, <Instance<typeof ScreenInfo>>{}),
        canShowLeftColumn: types.optional(types.boolean, false),
        isLeftColumnCollapsed: types.optional(types.boolean, false),
        todayTickets: types.optional(TodayTicketsStore, <Instance<typeof TodayTicketsStore>>{}),
        ticketList: types.optional(TicketListStore, <Instance<typeof TicketListStore>>{
            pager: <Instance<typeof ListPagerModel>>{}
        }),
        ticketDetail: types.optional(TicketDetailStore, defaultTicketDetailStore),
        ticketForVG: types.maybeNull(TicketDetailForVGStore),
        ticketListForVG: types.optional(TicketListForVGStore, <Instance<typeof TicketListForVGStore>>{
            pager: <Instance<typeof ListPagerModel>>{}
        }),
        ticketListTypeSelector: types.maybeNull(TicketListTypeSelectorStore),
        soundUI: types.optional(SoundUI, <Instance<typeof SoundUI>>{ isMute: true }),
        findTicketNumber: types.maybeNull(FindTicketByNumberStore),
        chat: types.optional(ChatListStore, {}),
        connectionStatus: types.string.create(ConnectionStatus.Connected.toString()),
        favoriteEventInfos: types.maybe(types.array(FavoriteEventInfo)),
        rightColumnBanners: types.maybeNull(RightColumnBannerStore),
        marketingAction: types.maybeNull(MarketingActionStoreDesktop)
    })
        .views(self => {
            const env = getEnv<ILiveAppEnv>(self);
            const entrancePageGlobalRenderTelemetry = createGlobalRenderTelemetryService("GRT Entrance page", "Desktop Live event list global", env.loggingService);
            return {
                get dateTimeService() {
                    return env.dateTimeService;
                },
                get settingsProvider() {
                    return env.settings;
                },
                get canShowAddPublicLinkToTicketToChat() {
                    return env.loggedUserId > 0 && self.chat.canShowChat;
                },
                get loggingService(): ILoggingService {
                    return env.loggingService;
                },
                get canShowMobileAppAdvert() {
                    return env.settings.canShowMobileAppAdvert;
                },
                get entryPageGlobalRenderTelemetry() {
                    return entrancePageGlobalRenderTelemetry;
                }
            }
        })
        .actions(self => {
            const env = getEnv<ILiveAppEnv>(self);
            const errorInterval = 10 * 1000;

            const initialize = () => {
                self.screenInfo.init();
                const pathsInstance = paths();
                const translation = translationForMts();

                self.ticketWrapper = MtsTicketStore.create(<SnapshotOut<typeof MtsTicketStore>>{}, env);
                self.ticketWrapper.init(5);

                self.findTicketNumber = __IsMTSTipos__ ? null : FindTicketByNumberStore.create(<Instance<typeof FindTicketByNumberStore>>{}, env); //TODOHMTS create setting

                if (env.settings.canShowVirtualGamesTickets) {
                    self.ticketListTypeSelector = TicketListTypeSelectorStore.create(<Instance<typeof TicketListTypeSelectorStore>>{
                        items: [TicketListTypeSelectorItemStore.create(<Instance<typeof TicketListTypeSelectorItemStore>>{ title: translation.myTickets_TypeSports, path: pathsInstance.ticketList }),
                        TicketListTypeSelectorItemStore.create(<Instance<typeof TicketListTypeSelectorItemStore>>{ title: translation.myTickets_TypeVirtualGames, path: pathsInstance.ticketListVG })],
                        activeIndex: 0
                    }, env);

                    self.ticketListForVG = TicketListForVGStore.create(<Instance<typeof TicketListForVGStore>>{
                        pager: <Instance<typeof ListPagerModel>>{}
                    }, env);

                    self.ticketForVG = defaultTicketDetailForVGStore();
                }

                self.rightColumnBanners = RightColumnBannerStore.create(<Instance<typeof RightColumnBannerStore>>{}, env);

                if (__IsMTSTipos__) {
                    self.marketingAction = MarketingActionStoreDesktop.create(<Instance<typeof MarketingActionStoreDesktop>>{}, env);
                }
                self.favoriteEventInfos = observable(getIdsFromLocalStorage());
            }

            const login = flow(function* () {
                let ctx: IGroupedApiCtx<ISportsBettingSessionApiForDesktop> | null = null;
                try {
                    if (!ctx || !ctx.isOpen()) {
                        ctx = env.sessionApi.open();
                    }
                    const response = yield ctx.api.playerSignInAsync({ login: '', password: '' });
                    env.loggingService.info('r', response);
                } catch (error) {
                } finally {
                    ctx = null;
                }
            });

            const addFavoriteEvent = (id: number) => {
                self.events.addFavoriteEvent(id);
                self.favoriteDetails.addEvent(id);

                if (self.favoriteEventInfos && !self.favoriteEventInfos.some(fi => fi.eventId === id)) {
                    self.favoriteEventInfos.push(FavoriteEventInfo.create(<Instance<typeof FavoriteEventInfo>>{ eventId: id }));
                }
                saveFavoriteIdsToLocalStorage();
            };

            const removeFavoriteEvent = (id: number) => {
                removeFavoriteEventRaw(id, true);
            }

            const removeFavoriteEventRaw = (id: number, canSaveToStorage: boolean) => {
                self.events.removeFavoriteEvent(id);
                self.favoriteDetails.removeEvent(id);

                const v = self.favoriteEventInfos.find(fi => fi.eventId === id);
                if (v) {
                    self.favoriteEventInfos.remove(v);
                }
                if (canSaveToStorage) {
                    saveFavoriteIdsToLocalStorage();
                }
            };

            const checkFavoriteIds = (isAfterInit: boolean): void => {
                if (self.events.state == ProgressStateModel.Success) {
                    let idsToRemove: Instance<typeof FavoriteEventInfo>[] = [];
                    self.favoriteEventInfos.forEach(fi => {
                        if (!self.events.favoriteCats.some(cat => cat.events.some(evt => evt.id === fi.eventId))) {
                            if (isAfterInit || fi.missingIteration > 20 /* ad hoc, one update 2-5 secs */) {
                                idsToRemove.push(fi);
                            } else {
                                fi.increaseMissing();
                            }
                        }
                    });
                    if (idsToRemove.length) {
                        idsToRemove.forEach(idToRemove => {
                            removeFavoriteEventRaw(idToRemove.eventId, false);
                        });
                        saveFavoriteIdsToLocalStorage();
                    }
                }
            }

            const removeAllFavoriteEvents = () => {
                self.events.removeAllFavoriteEvents();
                self.favoriteDetails.removeAllFavoriteEvents();
                self.favoriteEventInfos = observable([]);
                saveFavoriteIdsToLocalStorage();
            }

            const setEventIsFavorite = (id: number, value: boolean) => {
                if (value) {
                    addFavoriteEvent(id);
                } else {
                    removeFavoriteEvent(id);
                }
            };

            const setCategoryAsFavorite = (category: ILiveCategoryStore) => {
                category.events.forEach(event => { addFavoriteEvent(event.id); })
            }

            const toggleLeftColumnExpand = () => {
                self.isLeftColumnCollapsed = !self.isLeftColumnCollapsed;
            };

            /**
             * Subscribes to chat, sRNotificationHandler resubscribes if reconnected.
            */
            const subscribeToChatAsync: () => Promise<void> = flow(function* () {
                self.chat.setIsLoading();
                try {
                    let data: chatDtos.IChatSubscribeResponse = yield env.sRNotificationHandler.subscribeForChatMessagesAsync((data: IChatMessagesChangeData) => {
                        devLogService.log("subscribeToChatAsync callback [" + data?.messagesAll?.length + ", " + [data?.messagesUser?.length] + "]");
                        self.chat.setDataFromChange(data.messagesAll, data.messagesUser);
                    });
                    self.chat.setDataFromSubscribe(data);

                    env.sRNotificationHandler.registerOneNotificationHandlerForKey(NotificationHandlerKeys.StopChat,
                        () => {
                            self.chat.stopChat();
                            env.loggingService.warning("Hiding chat on stopChat");
                        });
                }
                catch (e) {
                    env.loggingService.error("subscribeToChat failed", e);  // can't do more, reconnect done in SRNotificationHandler
                    self.chat.setErrorWhenSubscribing();
                }
            });

            const handleCashoutChange = (data: cashOutDtos.CashOutResponse) => {
                devLogService.log("handleCashoutChange " + data.Bets.length);
                self.cashOutList.setData(data);
                self.ticketDetail.cashOutChanged(data);
                //self.todayTickets.setCashouts(data.Bets);
            };

            /**
                * Subscribes to MTS cashout, sRNotificationHandler resubscribes if reconnected.
            */
            const subscribeToMtsCashoutAsync: () => Promise<void> = flow(function* () {
                try {
                    self.cashOutList.setIsLoading();
                    let data: cashOutDtos.CashOutResponse = yield env.sRNotificationHandler.subscribeForCashoutAsync(handleCashoutChange);
                    handleCashoutChange(data);
                }
                catch (e) {
                    env.loggingService.error("subscribeToCashout failed", e);  // can't do more, reconnect done in SRNotificationHandler
                    self.cashOutList.setIsError();
                }
            });

            const setConnectionStatus = (connectionStatus: ConnectionStatus) => {
                self.connectionStatus = connectionStatus.toString();
            }

            const checkOfflineAsync: () => Promise<void> = flow(function* () {
                //same in stores
                if (self.isOfflineCheckInProgress) {
                    throw new Error("Already in action");
                }
                self.isOfflineCheckInProgress = true;
                try {
                    yield env.heartbeatService.forceCheckAsync();
                } finally {
                    self.isOfflineCheckInProgress = false;
                }
            });

            const saveFavoriteIdsToLocalStorage = (): void => {
                try {
                    env.sessionStorage.setItem(favoriteEventIdsStorageKey, JSON.stringify(self.favoriteEventInfos.map(fid => fid.eventId)));
                } catch (e) {
                    env.loggingService.error(e);
                }
            }

            const getIdsFromLocalStorage = (): Instance<typeof FavoriteEventInfo>[] => {
                try {
                    return (JSON.parse(env.sessionStorage.getItem(favoriteEventIdsStorageKey)) || []).map(
                        (id: any) => FavoriteEventInfo.create(<Instance<typeof FavoriteEventInfo>>{ eventId: id })
                    );
                }
                catch (e) {
                    env.loggingService.error(e);
                }
                return [];
            }

            return {
                initialize,
                saveError(error: Error, info: Object, isFatal: boolean) {
                    let stringifiedError = JSON.stringify({ msg: error.message, stack: error.stack }); //simple stringify(error) returns ""
                    let stringifiedInfo = JSON.stringify(info);
                    gAService.sendException(error.message + " / " + stringifiedError.substr(0, 100), isFatal);//https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#exDescription

                    const ctx = env.api.open();
                    ctx.api.reportProblemToServer(ReportedProblemType.Error, `${stringifiedError}: ${stringifiedInfo}`, errorInterval);
                },
                setCanShowLeftColumn(value: boolean): void {
                    self.canShowLeftColumn = value;
                },
                login,
                setEventIsFavorite,
                addFavoriteEvent,
                removeFavoriteEvent,
                toggleLeftColumnExpand,
                setCategoryAsFavorite,
                removeAllFavoriteEvents,
                subscribeToChatAsync,
                subscribeToMtsCashoutAsync,
                setConnectionStatus,
                checkOfflineAsync,
                checkFavoriteIds
            };
        }));
